// Brand Console GROWTH -- Diagnoses.
//
// AF UX audit P1B slice (docs/product/intelligence-console-ux-audit.md,
// Section 6) -- this page is migrated to the shared IntelligenceCard +
// LineageStrip pattern already proven on Recommendations (P0) and
// Signals (P1). See docs/product/intelligence-console-p1b-diagnoses-report.md
// for the full report, including the deliberate, source-driven field
// mapping decisions noted inline below.
//
// Reuses the existing, already-real AF-2.3 DiagnosisService entirely
// through its existing GET /intelligence/{workspace_id}/diagnoses route
// (diagnosesAPI.listForWorkspace) -- no new backend endpoint, no new
// service method, no new collection, no new query param.
//
// A diagnosis is Martian's own deterministic, evidence-scored HYPOTHESIS
// for why a signal fired (services.py's DiagnosisService, verified by
// direct read) -- never a proven cause. `causality` is stored on every
// real record as the literal string "NOT_PROVEN" (the only value this
// field will ever hold, per the service's own module docstring); this
// page renders it verbatim -- via the same StatusBadge used everywhere
// else in the app, whose NEUTRAL tone set already includes NOT_PROVEN --
// plus a plain-language gloss that is additive only, never a replacement
// for the raw value. "Signal" and "Entity" are the diagnosis's own real
// signal_id/entity_id, resolved against the workspace's existing
// signals/entities lists -- never guessed, shown as the raw id when a
// resolution genuinely isn't available.
//
// Field-mapping note (source of truth = the live diagnosis object, not
// the audit's own illustrative wording): a diagnosis has exactly ONE real
// narrative field, `hypothesis` -- there is no separate, distinct
// rationale/explanation string on the object. Per this task's own
// instruction to trust the live data contract over the audit's phrasing,
// `hypothesis` is used as the card's headline (as the audit's own words
// name it: "the existing real diagnosis title / hypothesis field") and is
// NOT duplicated into a second "statement" slot under a different label
// -- doing so would not add information, only make one real field look
// like two. The IntelligenceCard `statement` slot is left unset for this
// page; causality (a real, distinct field) takes the visually prominent
// position the audit intended for a rationale, since making causality
// unmistakable is this slice's actual truthfulness objective.
//
// Table functionality audit (Section 8): the pre-migration table provided
// (1) classification/status filtering -- real backend query params,
// unchanged below; (2) column sorting by confidence/created_at -- the
// exact same `sortRows`/`toggleSort` logic is preserved, now triggered by
// two small buttons instead of clickable <th> headers; (3) opening the
// detail Sheet -- preserved via an explicit "View full diagnosis" button
// per card (more discoverable than the old implicit whole-row click,
// which is impossible to reproduce as a single unambiguous target once
// cards -- not rows -- are the object, since a card can itself contain
// other interactive elements, like the LineageStrip's own signal link).
// Nothing here removes or narrows any pre-existing capability.
//
// P1.8: client resolution is still the shared useWorkspaceSelection()
// hook (unchanged from before this migration).

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { IntelligenceCard } from '@/components/brand/IntelligenceCard';
import { LineageStrip } from '@/components/brand/LineageStrip';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { diagnosesAPI, entitiesAPI, signalsAPI } from '@/lib/intelligenceApi';

function dash(v) {
  return v === null || v === undefined || v === '' ? '—' : v;
}
function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
function sortRows(rows, key, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
// Same sort mechanism the old <th> column headers triggered -- only the
// trigger control changed (a labeled button instead of a table header),
// since a card list has no header row to click.
function SortButton({ label, col, sort, onSort, testId }) {
  const active = sort.key === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      data-testid={testId}
      className={`h-8 inline-flex items-center gap-1 rounded-[var(--radius-button,6px)] border border-[var(--border)] px-2.5 text-[11px] font-medium ${active ? 'bg-[var(--surface-sunken,var(--canvas-secondary))] text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:bg-[var(--neutral-bg)]'}`}
    >
      Sort: {label}{active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </button>
  );
}

const CLASSIFICATIONS = [
  'TEMPORAL_CORRELATION', 'ENTITY_CORRELATION', 'SIGNAL_CORRELATION',
  'TEMPORAL_AND_ENTITY_CORRELATION', 'TEMPORAL_AND_SIGNAL_CORRELATION',
  'ENTITY_AND_SIGNAL_CORRELATION', 'TEMPORAL_ENTITY_AND_SIGNAL_CORRELATION',
  'STALENESS_CORRELATION', 'SOM_VISIBILITY_EVIDENCE', 'INSUFFICIENT_EVIDENCE',
];

export default function DiagnosesPage() {
  // P1.8: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch -- this page had the identical
  // defect the P1.7 audit fixed on Signals/Recommendations/Action Plans,
  // just missed in that pass.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const navigate = useNavigate();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [diagnoses, setDiagnoses] = useState(null); // null = loading
  const [entityNames, setEntityNames] = useState({});
  const [signalById, setSignalById] = useState({});
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ classification: '', status: '' });
  const [appliedFilters, setAppliedFilters] = useState({ classification: '', status: '' });

  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(null);

  const load = useCallback((activeFilters) => {
    if (!clientId) return;
    setDiagnoses(null);
    setError(null);
    const params = {};
    if (activeFilters.classification) params.classification = activeFilters.classification;
    if (activeFilters.status) params.status = activeFilters.status;
    Promise.all([
      diagnosesAPI.listForWorkspace(clientId, params),
      entitiesAPI.list(clientId, { includeArchived: true }).catch(() => ({ data: { entities: [] } })),
      signalsAPI.listForWorkspace(clientId).catch(() => ({ data: { signals: [] } })),
    ])
      .then(([diagRes, entRes, sigRes]) => {
        setDiagnoses(diagRes.data?.diagnoses || []);
        const names = {};
        (entRes.data?.entities || []).forEach((e) => { names[e.id] = e.canonical_name || e.id; });
        setEntityNames(names);
        const sigs = {};
        (sigRes.data?.signals || []).forEach((s) => { sigs[s.id] = s; });
        setSignalById(sigs);
      })
      .catch((e) => setError(e));
  }, [clientId]);

  useEffect(() => { load(appliedFilters); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    load(filters);
  };
  const clearFilters = () => {
    const cleared = { classification: '', status: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    load(cleared);
  };

  const sortedRows = useMemo(() => (diagnoses ? sortRows(diagnoses, sort.key, sort.dir) : []), [diagnoses, sort]);
  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  };

  const signalLabel = (id) => (id && signalById[id]?.title) || id || null;

  return (
    <DashboardLayout>
      <div data-testid="diagnoses-page" className="max-w-5xl mx-auto">
        <div className="mb-6">
          <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--accent)] mb-1">Why does Martian think this happened?</p>
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Diagnoses</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Martian's deterministic, evidence-scored hypotheses for why a signal fired — correlation only, never a proven cause.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Diagnoses appear once Martian has a workspace with detected signals." />}

        {!noClient && diagnoses === null && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={() => load(appliedFilters)} />}

        {!noClient && !error && diagnoses !== null && (
          <>
            <form onSubmit={applyFilters} className="flex items-end gap-2 flex-wrap mb-4" data-testid="diagnoses-filters">
              <div>
                <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Classification</label>
                <Select value={filters.classification || 'ANY'} onValueChange={(v) => setFilters((f) => ({ ...f, classification: v === 'ANY' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-diagnosis-classification" className="h-8 w-[260px] text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Any</SelectItem>
                    {CLASSIFICATIONS.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Status</label>
                <Select value={filters.status || 'ANY'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'ANY' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-diagnosis-status" className="h-8 w-[130px] text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Any</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="DISMISSED">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" size="sm" data-testid="apply-diagnosis-filters" className="h-8 text-[12px]">Apply</Button>
              <Button type="button" variant="outline" size="sm" data-testid="clear-diagnosis-filters" className="h-8 text-[12px]" onClick={clearFilters}>Clear filters</Button>
              <div className="flex items-center gap-2 ml-auto">
                <SortButton label="Confidence" col="confidence" sort={sort} onSort={toggleSort} testId="sort-diagnosis-confidence" />
                <SortButton label="Created" col="created_at" sort={sort} onSort={toggleSort} testId="sort-diagnosis-created" />
              </div>
            </form>

            {diagnoses.length === 0 && (
              <EmptyState
                testId="diagnoses-empty"
                title="No diagnoses yet"
                description="Martian generates a diagnosis only after a real signal fires -- none exist for this workspace yet matching these filters."
              />
            )}

            {diagnoses.length > 0 && (
              <div className="space-y-4" data-testid="diagnoses-list">
                {sortedRows.map((d) => {
                  const evidenceCount = Array.isArray(d.evidence) ? d.evidence.length : 0;
                  // P1 console polish (docs/product/intelligence-console-whole-chain-audit.md,
                  // P1 #2) -- clickable only when the signal actually
                  // resolved against the real, already-fetched
                  // signalById map (a genuine record, not merely a
                  // dangling id); otherwise the item stays inert text,
                  // exactly as before. Navigates to the Signals page
                  // itself (the same non-deep-linked pattern the
                  // pre-existing Signals -> Opportunity link already
                  // uses) -- no new fetch, no invented relationship.
                  const lineageItems = [];
                  if (d.signal_id) {
                    const resolvedSignal = signalById[d.signal_id];
                    lineageItems.push({
                      label: 'From signal',
                      value: signalLabel(d.signal_id),
                      testId: 'lineage-signal',
                      onClick: resolvedSignal ? () => navigate('/growth/signals') : undefined,
                    });
                  }

                  return (
                    <IntelligenceCard
                      key={d.id}
                      testId={`diagnosis-card-${d.id}`}
                      stage="DIAGNOSIS"
                      title={dash(d.hypothesis)}
                      badges={d.status && <StatusBadge value={d.status} />}
                      metrics={[
                        { label: 'Classification', value: dash(d.classification && d.classification.replace(/_/g, ' ')) },
                        { label: 'Entity', value: dash(entityNames[d.entity_id] || d.entity_id) },
                        d.confidence !== undefined && d.confidence !== null
                          ? { label: 'Confidence', value: <ConfidenceIndicator value={d.confidence} />, testId: 'intelligence-card-confidence' }
                          : null,
                        { label: 'Created', value: formatDateTime(d.created_at) },
                      ].filter(Boolean)}
                      lineage={(
                        <>
                          {d.causality && (
                            <div
                              className="mb-3 px-3 py-2 rounded border border-[var(--border)] bg-[var(--surface-sunken,var(--canvas-secondary))]"
                              data-testid="diagnosis-causality"
                            >
                              <p className="text-[9px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-1">Causality</p>
                              <StatusBadge value={d.causality} />
                              {d.causality === 'NOT_PROVEN' && (
                                <p className="text-[11px] text-[var(--text-secondary)] mt-1.5">Correlation only — not yet a proven cause.</p>
                              )}
                            </div>
                          )}
                          {evidenceCount > 0 && (
                            <p className="text-[11px] font-medium text-[var(--text-secondary)] mb-2" data-testid="diagnosis-evidence-count">
                              {evidenceCount} evidence item{evidenceCount === 1 ? '' : 's'} recorded
                            </p>
                          )}
                          <LineageStrip items={lineageItems} />
                        </>
                      )}
                      decision={(
                        <button
                          type="button"
                          data-testid="diagnosis-view-details"
                          onClick={() => setSelected(d)}
                          className="inline-flex items-center gap-1 rounded-[var(--radius-button,6px)] border border-[var(--border)] bg-[var(--surface-sunken,var(--canvas-secondary))] px-2.5 py-1 text-[11px] font-medium text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]"
                        >
                          View full diagnosis →
                        </button>
                      )}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Diagnosis detail -- unchanged from the pre-migration table:
            same Sheet, same fields, same open/close behavior, now reached
            via each card's explicit "View full diagnosis" button instead
            of an implicit whole-row click. ── */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="diagnosis-detail">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">{dash(selected.hypothesis)}</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored diagnosis record.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Entity</p><p className="text-[var(--text-primary)]">{dash(entityNames[selected.entity_id] || selected.entity_id)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Classification</p><p className="text-[var(--text-primary)] font-mono">{dash(selected.classification)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Confidence</p><ConfidenceIndicator value={selected.confidence} /></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selected.status ? <StatusBadge value={selected.status} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Causality</p><p className="text-[var(--text-primary)]" data-testid="detail-diagnosis-causality">{dash(selected.causality)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Created</p><p className="text-[var(--text-primary)]">{formatDateTime(selected.created_at)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Signal</p>
                    <div className="flex items-center gap-1.5">
                      <p className="text-[var(--text-primary)]" data-testid="detail-diagnosis-signal">{dash(signalLabel(selected.signal_id))}</p>
                      {/* P2.4 -- a diagnosis is never auto-dismissed when its
                          source signal resolves. Show the signal's current
                          status so an INSUFFICIENT_EVIDENCE / low-confidence
                          hypothesis for an already-resolved change reads
                          honestly rather than as a live concern. */}
                      {signalById[selected.signal_id]?.status && (
                        <StatusBadge value={signalById[selected.signal_id].status} />
                      )}
                    </div>
                    {signalById[selected.signal_id] && <p className="text-[10px] font-mono text-[var(--text-tertiary)] mt-0.5">ID: {selected.signal_id}</p>}
                  </div>
                  {Array.isArray(selected.evidence) && selected.evidence.length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Evidence</p>
                        <div className="space-y-1.5" data-testid="detail-diagnosis-evidence">
                          {selected.evidence.map((ev, i) => (
                            <div key={i} className="text-[11px] bg-[var(--surface-sunken,var(--canvas-secondary))] rounded px-2 py-1.5">
                              <p className="text-[var(--text-primary)]">{dash(ev.description)}</p>
                              {ev.type && <p className="text-[10px] font-mono text-[var(--text-tertiary)] mt-0.5">{ev.type}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {selected.metadata && (selected.metadata.related_entity_id || selected.metadata.related_entity_type) && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Related entity</p><p className="text-[var(--text-primary)]">{dash(entityNames[selected.metadata.related_entity_id] || selected.metadata.related_entity_id)}</p></div>
                        <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Related entity type</p><p className="text-[var(--text-primary)] font-mono">{dash(selected.metadata.related_entity_type)}</p></div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
